import type { ModelVoteCount } from '@meridian-ssa/shared';
import seedrandom from 'seedrandom';

/**
 * WeightedMatchEngine - Generates vote-weighted pairwise matches
 *
 * Prioritizes matches involving models with fewer historical votes using
 * the formula: P(match A-B) ∝ 1/√(votesA × votesB)
 *
 * Uses Efraimidis-Spirakis algorithm to establish a weighted ordering ONCE
 * at initialization. The active window slides through this fixed order as
 * matches are disqualified, ensuring stable window behavior.
 */

// ============ Types ============

export interface CandidateMatch {
  resultA_id: string;
  resultB_id: string;
  configA_id: string;
  configB_id: string;
  resultA_name: string;
  resultB_name: string;
}

export interface ValidResult {
  id: string;
  model_config_id: string;
  model_config_name: string;
}

export interface WeightedMatchEngineConfig {
  /** Seed for deterministic random selection (e.g., run ID) */
  seed: string;
  /** Global vote counts per model config */
  globalVoteCounts: ModelVoteCount[];
  /** Target number of matches in active window (default: 4) */
  targetWindowSize?: number;
}

// ============ WeightedMatchEngine ============

export class WeightedMatchEngine {
  private voteCountMap: Map<string, number>;
  private targetWindowSize: number;
  private seed: string;

  // Pairs sorted by weighted priority (computed once at initialization)
  private sortedPairs: CandidateMatch[] = [];
  private disqualifiedIndices: Set<number> = new Set();

  constructor(config: WeightedMatchEngineConfig) {
    this.seed = config.seed;
    this.targetWindowSize = config.targetWindowSize ?? 4;

    // Build vote count lookup with Laplace smoothing (+1 to handle 0-vote models)
    this.voteCountMap = new Map();
    for (const vc of config.globalVoteCounts) {
      this.voteCountMap.set(vc.model_config_id, vc.vote_count + 1);
    }
  }

  /**
   * Initialize engine with valid results, returns initial active window
   *
   * Generates all pairs, assigns weighted priority using Efraimidis-Spirakis,
   * and sorts them ONCE. This fixed order is maintained throughout the session.
   */
  initialize(results: ValidResult[]): CandidateMatch[] {
    // Create deterministic seed from run seed + result IDs
    const rng = seedrandom(this.seed);

    // Generate all unique pairs with their weighted keys
    const pairsWithKeys: Array<{ pair: CandidateMatch; key: number }> = [];

    for (let i = 0; i < results.length; i++) {
      for (let j = i + 1; j < results.length; j++) {
        const pair: CandidateMatch = {
          resultA_id: results[i].id,
          resultB_id: results[j].id,
          configA_id: results[i].model_config_id,
          configB_id: results[j].model_config_id,
          resultA_name: results[i].model_config_name,
          resultB_name: results[j].model_config_name,
        };

        // Calculate weight: 1 / sqrt(votesA * votesB)
        const votesA = this.voteCountMap.get(pair.configA_id) ?? 1;
        const votesB = this.voteCountMap.get(pair.configB_id) ?? 1;
        const weight = 1 / Math.sqrt(votesA * votesB);

        // Efraimidis-Spirakis key: random^(1/weight)
        // Higher weight => larger key on average => sorted earlier
        const key = Math.pow(rng(), 1 / weight);

        pairsWithKeys.push({ pair, key });
      }
    }

    // Sort by key descending (highest priority first) - this order is FIXED
    pairsWithKeys.sort((a, b) => b.key - a.key);
    this.sortedPairs = pairsWithKeys.map(p => p.pair);

    return this.getActiveWindow();
  }

  /**
   * Disqualify all matches containing this result (e.g., when model fails)
   * Returns the new active window (slides forward in fixed order)
   */
  disqualifyResult(resultId: string): CandidateMatch[] {
    // Mark all pairs containing this result as disqualified
    for (let idx = 0; idx < this.sortedPairs.length; idx++) {
      const pair = this.sortedPairs[idx];
      if (pair.resultA_id === resultId || pair.resultB_id === resultId) {
        this.disqualifiedIndices.add(idx);
      }
    }

    return this.getActiveWindow();
  }

  /**
   * Get current active window of matches
   *
   * Returns the first N non-disqualified pairs from the fixed sorted order.
   * This ensures window stability - disqualifications only cause the window
   * to slide forward, never to reshuffle.
   */
  getActiveWindow(): CandidateMatch[] {
    const window: CandidateMatch[] = [];

    for (let idx = 0; idx < this.sortedPairs.length; idx++) {
      if (this.disqualifiedIndices.has(idx)) continue;

      window.push(this.sortedPairs[idx]);

      if (window.length >= this.targetWindowSize) break;
    }

    return window;
  }

  /**
   * Check if there are more valid matches beyond the current window
   */
  hasMoreMatches(): boolean {
    let availableCount = 0;
    for (let idx = 0; idx < this.sortedPairs.length; idx++) {
      if (!this.disqualifiedIndices.has(idx)) {
        availableCount++;
      }
    }
    return availableCount > this.targetWindowSize;
  }

  /**
   * Get count of valid pairs not currently in the window
   */
  getRemainingCount(): number {
    let availableCount = 0;
    for (let idx = 0; idx < this.sortedPairs.length; idx++) {
      if (!this.disqualifiedIndices.has(idx)) {
        availableCount++;
      }
    }
    return Math.max(0, availableCount - this.targetWindowSize);
  }
}
